/**
 * Customer-portal session store.
 *
 * Lives entirely separate from the staff Redux auth slice — the portal
 * can be embedded inside the same web app without leaking customer
 * tokens into the staff JWT and vice-versa. localStorage-backed so a
 * customer who reloads stays signed in.
 */

import { useEffect, useState } from 'react';

export interface PortalSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  /** Customer identity decoded from the AuthResponse. */
  customerId: string;
  customerName: string;
  email?: string;
  phone?: string;
  tenantCode: string;
  tenantId: string;
}

const STORAGE_KEY = 'kss.portal.session';

let active: PortalSession | null = readPersisted();
const listeners = new Set<(s: PortalSession | null) => void>();

function readPersisted(): PortalSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PortalSession;
    if (!parsed?.accessToken) return null;
    // Drop expired sessions on read.
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(s: PortalSession | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (s) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getPortalSession(): PortalSession | null {
  return active;
}

export function setPortalSession(s: PortalSession | null): void {
  active = s;
  persist(s);
  for (const l of listeners) l(s);
}

export function subscribe(fn: (s: PortalSession | null) => void): () => void {
  listeners.add(fn);
  fn(active);
  return () => listeners.delete(fn);
}

export function usePortalSession(): PortalSession | null {
  const [s, setS] = useState<PortalSession | null>(active);
  useEffect(() => subscribe(setS), []);
  return s;
}
